import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { urgentTasksAPI } from '@/lib/api';
import { AlertTriangle, Clock, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';

const SESSION_KEY = 'urgent_tasks_modal_shown';

function fmtDeadline(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export function UrgentTaskGate() {
  const { user, isAuthenticated } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(null);

  const gated = isAuthenticated && user && !user.force_password_change;

  const load = useCallback(async () => {
    if (!gated) return;
    try {
      const { data } = await urgentTasksAPI.active();
      const items = data?.items || [];
      setTasks(items);
      if (items.length && !sessionStorage.getItem(SESSION_KEY)) {
        setShowModal(true);
        sessionStorage.setItem(SESSION_KEY, '1');
      }
    } catch { /* non-blocking */ }
  }, [gated]);

  useEffect(() => {
    if (!gated) return;
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [gated, load]);

  const markDone = async (task) => {
    setBusy(task.task_id);
    try {
      await urgentTasksAPI.complete(task.task_id);
      setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
      toast.success('Marked as completed');
    } catch {
      toast.error('Could not update task');
    } finally {
      setBusy(null);
    }
  };

  if (!gated || tasks.length === 0) return null;

  const TaskRow = ({ t, compact }) => (
    <div
      data-testid={`urgent-task-${t.task_id}`}
      className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${
        t.is_overdue ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
            t.is_overdue ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
          }`}>
            {t.is_overdue ? 'Overdue' : t.priority}
          </span>
          <span className="text-xs text-slate-500">{t.task_type}</span>
        </div>
        <p className="font-semibold text-slate-900 mt-1 truncate">{t.title}</p>
        {!compact && t.description ? (
          <p className="text-sm text-slate-600 mt-0.5 line-clamp-2">{t.description}</p>
        ) : null}
        <p className="flex items-center gap-1 text-xs text-slate-500 mt-1">
          <Clock size={12} /> Due {fmtDeadline(t.deadline)}
        </p>
      </div>
      <button
        data-testid={`urgent-task-done-${t.task_id}`}
        onClick={() => markDone(t)}
        disabled={busy === t.task_id}
        className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-2 hover:bg-emerald-700 disabled:opacity-60"
      >
        <CheckCircle2 size={14} /> Completed
      </button>
    </div>
  );

  return (
    <>
      {/* Persistent banner while there are unresolved urgent tasks */}
      <div
        data-testid="urgent-task-banner"
        className="fixed top-0 inset-x-0 z-[60] bg-red-600 text-white"
      >
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle size={16} />
            {tasks.length} urgent {tasks.length === 1 ? 'task requires' : 'tasks require'} your action
          </div>
          <button
            data-testid="urgent-task-banner-open"
            onClick={() => setShowModal(true)}
            className="text-xs font-bold bg-white/20 hover:bg-white/30 rounded px-3 py-1"
          >
            Review now
          </button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
             data-testid="urgent-task-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-600" /> Action Required
              </h2>
              <button data-testid="urgent-task-modal-close" onClick={() => setShowModal(false)}
                      className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              {tasks.map((t) => <TaskRow key={t.task_id} t={t} />)}
            </div>
            <div className="p-3 border-t flex items-center justify-between gap-3">
              <span className="text-xs text-slate-500">
                Unresolved tasks stay until marked Completed.
              </span>
              <button
                data-testid="urgent-task-modal-later"
                onClick={() => setShowModal(false)}
                className="shrink-0 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white text-slate-700 text-xs font-semibold px-4 py-2 hover:bg-slate-50"
              >
                <X size={14} /> Close for now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UrgentTaskGate;
